import { EmptyState, Icon, Tag } from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { auditSubjectWords, codeAsWords } from '../../lib/audit-words.ts';
import { stamp } from './format.ts';
import { describeUserAgent } from './userAgent.ts';
import { ADMIN_SESSION_ENDED_BY, AUDIT_ACTION } from './vocabulary.ts';
import { SectionLabel } from './columns.tsx';
import type { ActionsPayload, SessionsPayload } from './types.ts';

/**
 * The drawer's two remaining tables: ACTIVE SESSIONS and RECENT ACTIONS.
 *
 * WHAT "ACTIVE SESSIONS" HONESTLY MEANS HERE. A signed-in console holds a
 * signed cookie carrying a `tv` claim, and every request re-reads
 * identity.admin_user to check it, so ending every session moves one integer.
 * That integer is shown, because "Sign out all sessions" moves it and an
 * operator who presses that button should be able to see that it moved. Since
 * ADR-0008 every request is ALSO checked against the session's own row in
 * identity.refresh_token (below): a row that is revoked or expired is refused,
 * so ending one session refuses every copy of its cookie. This note used to
 * say there was no server-side session table; there is, and the guard reads it.
 *
 * What the platform DOES record per device is two tables, and both are drawn:
 *   · identity.device_trust — written when somebody ticks "trust this device 30
 *     days", carrying the raw user agent, an expiry and a revocation;
 *   · identity.refresh_token — one FAMILY per sign-in, carrying the user agent
 *     and IP. The backoffice sign-in handler INSERTs one row per sign-in, so a
 *     family IS a session here. The table is named for a rotation this platform
 *     does not do yet — there is one access token, not a short one refreshed by
 *     a long one — so `family_id` is the session's own identifier and no
 *     successor ever rotates into it.
 *
 * AND THIS NOTE USED TO SAY THAT NOTHING WROTE THAT TABLE. That stopped being
 * true the day the INSERT landed, and nothing failed when it did: a comment
 * cannot be tested, so the next person to read it would have been told the
 * opposite of what the screen was showing them. The empty state below is
 * therefore worded as a fact about the ADMIN — nobody has signed in since the
 * recording started — and says outright that the read succeeded, because that
 * is the one claim that stays true however the writing side changes.
 *
 * AND NOTHING IS ENRICHED. The design draws "Chrome · macOS · Makati office ·
 * 112.198.x.x". Two of those four are recorded and two are not. The user agent
 * is parsed locally by userAgent.ts and shown BESIDE the raw string it was
 * parsed from, so the claim can always be checked; the IP is printed exactly as
 * stored. No geo lookup happens, because there is no geo service here and a
 * location invented from an address is a location somebody would act on.
 */
export function SessionsPanel({
  sessions,
  loading,
  failed,
  actions,
  actionsLoading,
  actionsFailed,
  canReadActions,
}: {
  sessions: SessionsPayload | null;
  loading: boolean;
  failed: boolean;
  actions: ActionsPayload | null;
  actionsLoading: boolean;
  actionsFailed: boolean;
  canReadActions: boolean;
}) {
  const families = sessions?.refreshFamilies ?? [];
  const devices = sessions?.trustedDevices ?? [];
  /*
   * WHOSE ENTRIES THE LIST READ. For a reader whose access covers every brand the API reads every
   * entry this admin made; for one restricted to some brands it reads only the entries recorded
   * against those brands — none recorded against no brand, which is where a role change or a
   * suspension is recorded — and names them in `restrictedTo`, since it is what applied the
   * restriction. "The trail holds nothing with this admin as the person who acted", said to a
   * restricted reader, would describe entries they were never given, so every sentence below about
   * what was read takes its words from this. Null, or an answer that does not carry it: every brand.
   */
  const actionBrands = actions?.restrictedTo ?? null;
  const actionScope =
    actionBrands === null
      ? null
      : `Your access is restricted to ${actionBrands.join(', ')}, so this list covers ${
          actionBrands.length === 1 ? 'that brand' : 'those brands'
        } alone`;

  return (
    <>
      <SectionLabel>ACTIVE SESSIONS</SectionLabel>

      {failed ? (
        /* GET /api/access/admins/:id/sessions did not answer. The path belongs
           in here rather than on the screen: an operator cannot call it, cannot
           look it up and cannot repeat it to anybody who could. What they do
           need from this state is the one thing the blank below cannot say for
           itself — that it is this screen failing and not a fact about the
           admin — so that is what the sentence says instead of the path. */
        <EmptyState icon="warning-circle" title="Sessions could not be read">
          The request for this admin&rsquo;s sessions did not come back, so nothing below was
          read. That is this screen failing, not their account: it says nothing about whether they
          are signed in anywhere.
        </EmptyState>
      ) : loading && sessions === null ? (
        <div className="skeleton" style={{ height: '90px' }}>
          &nbsp;
        </div>
      ) : (
        <>
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              Each sign-in is listed below as a session, and every click is checked against it: a
              session that has ended is refused. &ldquo;Sign out all sessions&rdquo; ends them all
              at the holder&rsquo;s next click, and it also moves this account&rsquo;s session
              version, now <span className="tabular">{sessions?.admin.tokenVersion ?? '—'}</span>,
              which every click is checked against too, so you can see that it moved.
            </div>
          </div>

          <div className="session-row">
            <div>
              <strong>Last sign-in</strong>
              <div className="text-muted">
                {sessions?.admin.lastSignInAt
                  ? `${stamp(sessions.admin.lastSignInAt)}${sessions.admin.lastSignInLabel ? ` · ${sessions.admin.lastSignInLabel}` : ''}`
                  : 'never'}
              </div>
            </div>
            <span className="text-subtle tabular">{sessions?.admin.lastSignInIp ?? 'no IP recorded'}</span>
          </div>

          {devices.length > 0 && (
            <div>
              <SectionLabel>TRUSTED DEVICES</SectionLabel>
              {devices.map((d) => (
                <div className="session-row" key={d.id}>
                  <Tag tone={d.live ? 'accent' : 'quiet'}>{d.live ? 'Live' : 'Ended'}</Tag>
                  <div>
                    {describeUserAgent(d.userAgent)}
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      trusted {stamp(d.createdAt)} · {d.revokedAt ? `revoked ${stamp(d.revokedAt)}` : `expires ${stamp(d.expiresAt)}`}
                    </div>
                  </div>
                  {/* The parse is shown beside what it was parsed from, always. */}
                  {d.userAgent && <div className="session-agent">{d.userAgent}</div>}
                </div>
              ))}
            </div>
          )}

          {families.length > 0 ? (
            <div>
              <SectionLabel>REFRESH TOKEN FAMILIES</SectionLabel>
              {families.map((f) => (
                <div className="session-row" key={f.familyId}>
                  <Tag tone={f.live ? 'accent' : 'quiet'}>{f.live ? 'Live' : 'Ended'}</Tag>
                  <div>
                    {describeUserAgent(f.userAgent)}
                    <div className="text-subtle" style={{ fontSize: '11px' }}>
                      started {stamp(f.startedAt)} · {formatCount(f.tokens)} token
                      {f.tokens === 1 ? '' : 's'}
                      {f.revokedReason
                        ? ` · ${ADMIN_SESSION_ENDED_BY[f.revokedReason] ?? `ended: ${codeAsWords(f.revokedReason).toLowerCase()}`}`
                        : ''}
                      {f.ip ? ` · ${f.ip}` : ''}
                    </div>
                  </div>
                  {f.userAgent && <div className="session-agent">{f.userAgent}</div>}
                </div>
              ))}
            </div>
          ) : (
            /* identity.refresh_token, grouped by family, came back with no rows
               for this admin. The table name moves in here because it is of no
               use to the person reading the notice — it is not something they
               can look up, ask for, or quote to support — while the DISTINCTION
               it was carrying is the whole value of this state: the read worked
               and found nothing, which is a different thing from the read having
               failed. The failed case is its own branch at the top of this panel
               and says so in its own words; neither state is allowed to be
               mistakable for the other. */
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                No sessions are recorded for this admin, and that is a real nothing rather than a
                failed read — the list came back, it was simply empty. Signing in records one entry
                per session, with the browser it was made from and the IP address it came from, so
                an empty list here means this admin has not signed in since that recording started.
                Their sign-in history is in the trail below and on the Audit log tab.
              </div>
            </div>
          )}
        </>
      )}

      {/* WHAT "ENDED" MEANS, said as an operator acts on it. This notice used to
          call "Ended" a record and not a lock: a copied cookie of a signed-out
          session was accepted until its own expiry, because the guard read only
          admin_user.token_version. ADR-0008 made the guard (loadPrincipal,
          plugins/session.ts) refuse any request whose refresh_token row is
          revoked or expired, so ending one session now refuses every copy of it
          at its next request — proved by signout-revocation.integration.test.ts.
          The console offers no control that ends one session of another admin:
          that is their own sign-out, so the advice is Sign out all sessions. */}
      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          <strong>&ldquo;Ended&rdquo; means that session can no longer be used.</strong> A session
          ends when it runs out, when the admin signs out in that browser, or when every session is
          ended at once. From then on it is refused at its next click, and so is any copy of that
          sign-in somebody may have taken. Suspending the account or pressing{' '}
          <strong>Sign out all sessions</strong> ends every session at once, so when you think
          somebody else may be using one of this admin&rsquo;s sign-ins, use Sign out all sessions.
        </div>
      </div>

      <SectionLabel>RECENT ACTIONS</SectionLabel>
      {!canReadActions ? (
        /* A read of platform.audit_log filtered to this admin as the actor,
           gated on audit.export and not on access.manage. Only the TABLE name
           moves into this comment: the two permission keys stay on the screen on
           purpose, because an operator can find both of them on the permission
           matrix they administer and can go and ask somebody for the one they
           are missing. A name you can act on is not jargon; a name you cannot is
           the only kind this rule is about. */
        <EmptyState icon="lock-key" title="Recent actions need audit.export">
          This list is this admin&rsquo;s own entries in the platform&rsquo;s audit trail, and
          reading what somebody DID is a different permission from changing what they MAY DO. Your
          role holds access.manage but not audit.export, so the roster is yours and this list is
          not.
        </EmptyState>
      ) : actionsFailed ? (
        /* GET /api/access/admins/:id/actions did not answer — the path is here
           for whoever debugs it, and the sentence is for whoever is looking at
           the gap it leaves. It must not drift towards sounding like an empty
           trail: the genuinely-empty case is the branch below. */
        <EmptyState icon="warning-circle" title="Recent actions could not be read">
          The request for this admin&rsquo;s recent actions did not come back. Their trail is
          unchanged and simply unknown here until the read succeeds.
        </EmptyState>
      ) : actionsLoading && actions === null ? (
        <div className="skeleton" style={{ height: '120px' }}>
          &nbsp;
        </div>
      ) : (actions?.items.length ?? 0) === 0 ? (
        /* platform.audit_log, filtered to this admin as the actor, returned zero
           rows. The name is of no use to an operator and the reason this state
           exists is: it has to say that the read SUCCEEDED, because the branch
           directly above it is the read having failed and the two look identical
           on screen unless the words separate them. It also has to say why an
           honestly-empty trail is normal here, or somebody reads it as a system
           that has lost their colleague's history. */
        actionBrands === null ? (
          <EmptyState icon="list-checks" title="Nothing in the audit log for this admin">
            The audit trail was read and holds nothing with this admin as the person who acted. That
            is a fact about them rather than about the read: an admin who has only ever looked at
            screens leaves nothing here, because the trail records the changes people make and not
            the screens they open.
          </EmptyState>
        ) : (
          /* Restricted: the read covered the reader's brands, so its emptiness is a fact about
             those brands, not about the admin. */
          <EmptyState
            icon="list-checks"
            title={`Nothing recorded against ${actionBrands.join(', ')} for this admin`}
          >
            {actionScope}, and the audit trail holds no entry recorded against{' '}
            {actionBrands.length === 1 ? 'it' : 'them'} with this admin as the person who acted. An
            entry this admin recorded against any other brand is not read here, and neither is one
            recorded against no brand, such as a role change or a suspension, so this says nothing
            about those.
          </EmptyState>
        )
      ) : (
        <>
          {actionScope !== null && (
            <div className="notice notice-quiet">
              <Icon name="info" />
              <div>
                {actionScope}: an entry this admin recorded against any other brand is not read
                here, and neither is one recorded against no brand, such as a role change or a
                suspension.
              </div>
            </div>
          )}
          {(actions?.items ?? []).map((a) => (
            <div className="session-row" key={a.seq}>
              <span className="tabular text-muted">{stamp(a.at)}</span>
              <div>
                {/* In words, as the record below is: an action nobody has
                    written a label for is said as words by codeAsWords, never
                    printed as the log's code. */}
                {AUDIT_ACTION[a.action] ?? codeAsWords(a.action)}
                {/* `seq` is platform.audit_log's hash-chained sequence number,
                    and the number itself is worth showing — it is what support
                    asks for to find one entry again, and gaps in it are what
                    make the chain checkable. The word "seq" is not: it is the
                    column's name, abbreviated, and "entry" says the same thing
                    to somebody who has never seen the column. Nor is the
                    subject's code (admin_user, maintenance_window): the record
                    is named in the console's words for it. */}
                <div className="text-subtle" style={{ fontSize: '11px' }}>
                  {auditSubjectWords(a.subjectType)}
                  {a.brand ? ` · ${a.brand}` : ''}
                  {a.ip ? ` · ${a.ip}` : ''} · entry {a.seq}
                </div>
              </div>
            </div>
          ))}
          {/* The route orders by platform.audit_log.seq descending and caps the
              result at what `limit` reports; the (actor_id, created_at) index is
              what makes the per-actor read cheap. None of those three names means
              anything to an operator. The CAP does, and it is the only part of
              that sentence they can act on — this list is a window on the trail
              and not the whole of it — so the line keeps the number, drops the
              index, and names the control that gets them the rest. "Any older
              entries", not "Older entries exist": the list does not know whether
              there are more than it shows, and an admin with three entries has
              none. */}
          <div className="text-subtle" style={{ fontSize: '11px' }}>
            The {formatCount(actions?.limit ?? 0)} most recent
            {actionBrands === null ? '' : ` recorded against ${actionBrands.join(', ')}`}, newest
            first. Any older entries are reachable through <strong>Export audit log</strong>.
          </div>
        </>
      )}
    </>
  );
}
